import { breakpoints } from "@certn/cascadia-tokens";
import { getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import { Button } from "react-aria-components";

import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { useLayout } from "../../../foundations/grid-layout/Layout";
import { MediaQuery } from "../../../utils/MediaQuery";
import { ButtonGroup } from "../../button/ButtonGroup";
import { GhostIconButton } from "../../button/GhostIconButton";

import { Search } from "../../search/Search";
import { NavigationBar } from "./NavigationBar";
import { NavLogo, NavLogoSlotProvider } from "./NavLogo";

interface PrimaryNavProps {
  testID: string;
  searchPlaceholder?: string;
  onLogoPress?(): void;
  logo?: React.ReactNode;
  children?: React.ReactNode;
}

export function PrimaryTopNav(props: PrimaryNavProps) {
  const { testID, children } = props;
  const isSmallScreen = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-md"].maxWidth.toString()}px)`
  );
  const { toggleMenu, menuPosition, hasNavigation } = useLayout();
  const { search, setSearch } = useLayout();

  if (search !== undefined && isSmallScreen) {
    return (
      <NavigationBar testID={`${testID}-searching`}>
        <GhostIconButton
          icon="arrow_back"
          color="icon-weak"
          onPress={() => setSearch(undefined)}
        />
        <ButtonGroup>
          <Search
            autoFocus
            name="search"
            value={search}
            onChange={(value) => {
              setSearch(value.length > 0 ? value : undefined);
            }}
            testID="top-nav-search"
            compact={false}
          />
        </ButtonGroup>
      </NavigationBar>
    );
  }

  return (
    <NavigationBar testID={testID}>
      <MediaQuery type="show" sm md>
        <ButtonGroup justify="end">
          {menuPosition === "left" && hasNavigation && (
            <MediaQuery type="show" sm md>
              <GhostIconButton icon="menu" onPress={() => toggleMenu(true)} />
            </MediaQuery>
          )}
          <NavLogoSlotProvider slot="logo">
            <Button
              className={css(
                styles.logo,
                styles.logoSmMd,
                {
                  "&[data-focus-visible]": {
                    boxShadow: "var(--focus-action)",
                  },
                },
                props.onLogoPress && { cursor: "pointer" }
              )}
              onPress={props.onLogoPress}
            >
              {props.logo || <NavLogo />}
            </Button>
          </NavLogoSlotProvider>
        </ButtonGroup>
      </MediaQuery>
      <MediaQuery type="hide" sm md>
        <NavLogoSlotProvider slot="full">
          <Button
            className={css(
              styles.logo,
              styles.logoLg,
              getRegionKey() === "au" && styles.logoLgIntercheck,
              {
                "&[data-focus-visible]": {
                  boxShadow: "var(--focus-action)",
                },
              },
              props.onLogoPress && { cursor: "pointer" }
            )}
            onPress={props.onLogoPress}
          >
            {props.logo || <NavLogo />}
          </Button>
        </NavLogoSlotProvider>
      </MediaQuery>
      <ButtonGroup justify="end">
        {children}
        {menuPosition === "right" && hasNavigation && (
          <MediaQuery type="show" sm md>
            <GhostIconButton icon="menu" onPress={() => toggleMenu(true)} />
          </MediaQuery>
        )}
      </ButtonGroup>
    </NavigationBar>
  );
}

const styles = {
  children: css({ display: "flex" }),
  logo: css({
    background: "transparent",
    border: "none",
    outline: "none",
    height: "32px",
    cursor: "pointer",
    padding: 0,
    margin: 0,
    borderRadius: "var(--border-radius-2)",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  }),
  logoSmMd: css({
    aspectRatio: "1/1",
    "&:has(img)": {
      aspectRatio: "auto",
      width: "auto",
      maxWidth: "140px",
    },
  }),
  logoLg: css({
    aspectRatio: "84/24",
    "&:has(img)": {
      aspectRatio: "auto",
      width: "auto",
      maxWidth: "200px",
    },
  }),
  logoLgIntercheck: css({
    aspectRatio: "94/24",
  }),
};
